import { useEffect, useState } from 'react';
import { listRooms, updateRoom, deleteRoom, getRoomImages } from '../api/rooms';
import { useSidebar } from '../contexts/SidebarContext';

/**
 * Recent Projects Component
 * Displays recent project cards with images and metadata
 */

const RecentProjects = () => {
  const { setCurrentView, setRoomId, setPhotorealisticImage } = useSidebar();
  const [rooms, setRooms] = useState([]);
  const [menuRoomId, setMenuRoomId] = useState(null);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const projects = [
    {
      id: 1,
      title: 'My Bedroom Interior',
      createdAt: new Date().toISOString(),
      image: '/api/placeholder/400/300',
    },
    {
      id: 2,
      title: 'My Living Room Interior',
      createdAt: new Date().toISOString(),
      image: '/api/placeholder/400/300',
    },
    {
      id: 3,
      title: 'My Kitchen Interior',
      createdAt: new Date().toISOString(),
      image: '/api/placeholder/400/300',
    },
  ];

  // Close Update Details modal on Escape
  useEffect(() => {
    if (editingRoomId == null) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setEditingRoomId(null);
        setError(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editingRoomId]);

  useEffect(() => {
    listRooms()
      .then((data) => {
        // Debug: verify GET /rooms payload shape
        console.log('RecentProjects: GET /rooms data:', data);
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (Array.isArray(data?.results)) {
          items = data.results;
        } else if (Array.isArray(data?.rooms)) {
          items = data.rooms;
        }

        // Sort by updatedAt (newest first), with fallbacks for different field casings
        const sorted = [...items].sort((a, b) => {
          const aDate = a.updatedAt || a.createdAt || 0;
          const bDate = b.updatedAt || b.createdAt || 0;
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });

        setRooms(sorted);

        // Prefetch latest image thumbnails for the most recent rooms
        const toPrefetch = sorted.slice(0, 6);
        toPrefetch.forEach((room) => {
          const id = room.id;
          if (id == null) return;
          getRoomImages(id)
            .then((images) => {
              if (!Array.isArray(images) || images.length === 0) return;
              const latestImageId =
                room.latestRoomImageId ?? room.latestImageId ?? room.roomImageId ?? null;
              let chosen = null;
              if (latestImageId != null) {
                chosen =
                  images.find(
                    (img) =>
                      img.id === latestImageId || img.roomImageId === latestImageId
                  ) ?? null;
              }
              if (!chosen) {
                const sortedImgs = [...images].sort((a, b) => {
                  const at = a.createdAt ?? '';
                  const bt = b.createdAt ?? '';
                  return new Date(bt) - new Date(at);
                });
                chosen = sortedImgs[0] ?? null;
              }
              const url = chosen?.s3Key ?? null;
              if (!url) return;
              setRooms((prev) =>
                prev.map((r) =>
                  r.id === id ? { ...r, _thumbnailUrl: url } : r
                )
              );
            })
            .catch((err) => {
              console.error('RecentProjects: GET /rooms/{id}/images failed:', err);
            });
        });
      })
      .catch((err) => {
        console.error('RecentProjects: GET /rooms failed:', err);
      });
  }, []);

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Recent Projects</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(rooms.length > 0 ? rooms : projects).slice(0, 3).map((item, index) => {
          const id = item.id ?? index;
          const title = item.name ?? item.title ?? `Room ${id}`;
          const rawCreated = item.createdAt;
          const created =
            typeof rawCreated === 'string'
              ? new Date(rawCreated).toLocaleDateString()
              : rawCreated ?? '';
          const thumbnailUrl = item._thumbnailUrl;
          const isMenuOpen = menuRoomId === id;
          return (
          <div
            key={id}
            className="bg-gray-100 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
            onClick={() => {
              // Navigate to Photorealistic view for this room: /design/{room_id}
              setRoomId(id);
              setPhotorealisticImage(null);
              setCurrentView('Photorealistic');
            }}
          >
            {/* Thumbnail (latest room image) or placeholder */}
            <div className="aspect-video bg-gray-200 flex items-center justify-center relative overflow-hidden">
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  draggable={false}
                  crossOrigin="anonymous"
                  onError={(e) => {
                    // If the thumbnail fails to load (e.g., COEP/CORS or expired URL),
                    // fall back to the placeholder so we don't show a broken image icon.
                    e.currentTarget.onerror = null;
                    setRooms((prev) =>
                      prev.map((r) =>
                        r.id === id ? { ...r, _thumbnailUrl: null } : r
                      )
                    );
                  }}
                />
              ) : (
                <>
                  <svg
                    className="w-24 h-24 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-gray-400 text-6xl">🏔️</div>
                  </div>
                </>
              )}
            </div>

            {/* Project Info */}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 mb-1 group-hover:text-primary-200 transition-colors truncate">
                    {title}
                  </h3>
                  {created && (
                    <p className="text-xs text-gray-500">
                      Created: {created}
                    </p>
                  )}
                </div>
                <div className="relative shrink-0 z-10">
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                    aria-expanded={isMenuOpen}
                    aria-haspopup="true"
                    aria-label="Project actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMenuOpen) {
                        setMenuRoomId(null);
                      } else {
                        setMenuRoomId(id);
                      }
                    }}
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </button>
                  {isMenuOpen && (
                    <div className="absolute bottom-full right-0 mb-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-20">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuRoomId(null);
                          setEditingRoomId(id);
                          setEditName(item.name ?? title);
                          setEditDescription(item.description ?? '');
                        }}
                      >
                        Update Details
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setMenuRoomId(null);
                          if (!window.confirm('Are you sure you want to delete this room?')) return;
                          setError(null);
                          try {
                            await deleteRoom(id);
                            setRooms((prev) => prev.filter((r) => r.id !== id));
                          } catch (err) {
                            console.error('Delete room failed:', err);
                            setError(err.message ?? 'Failed to delete room');
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );})}
      </div>

      {/* Update Details modal */}
      {editingRoomId != null && (() => {
        const list = rooms.length > 0 ? rooms : projects;
        const room = list.find((r) => r.id === editingRoomId);
        const roomTitle = room?.name ?? room?.title ?? `Room ${editingRoomId}`;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => {
              setEditingRoomId(null);
              setError(null);
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="update-details-title"
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="update-details-title" className="text-lg font-semibold text-gray-800 mb-4">
                Update room details
              </h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-room-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    id="edit-room-name"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                    placeholder="e.g. Living Room"
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="edit-room-desc" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="edit-room-desc"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-y min-h-[80px]"
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    disabled={saving}
                    className="flex-1 py-2.5 px-4 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    onClick={async () => {
                      setError(null);
                      setSaving(true);
                      try {
                        await updateRoom(editingRoomId, {
                          name: editName.trim() || roomTitle,
                          description: editDescription.trim(),
                          latestImageId: room?.latestImageId ?? room?.roomImageId ?? null,
                        });
                        setRooms((prev) =>
                          prev.map((r) =>
                            r.id === editingRoomId
                              ? { ...r, name: editName.trim() || roomTitle, description: editDescription.trim() }
                              : r
                          )
                        );
                        setEditingRoomId(null);
                      } catch (err) {
                        console.error('Update room failed:', err);
                        setError(err.message ?? 'Failed to update room');
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 disabled:opacity-60 transition-colors"
                    onClick={() => {
                      setEditingRoomId(null);
                      setError(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RecentProjects;
